import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { lvApi } from "../services/lvService";
import { selectCurrentProjectId } from "./projectDetailsSlice";

/** Load LV for the editor. gaebData (AVA ProjectDto) also stored. */
export const fetchBoQEditorData = createAsyncThunk(
	"boq/fetchEditorData",
	async (lvId, { dispatch, rejectWithValue, getState }) => {
		const projectId = selectCurrentProjectId(getState());
		if (!projectId) return rejectWithValue("No project context");
		const queryResult = dispatch(
			lvApi.endpoints.getLVById.initiate({ projectId, lvId }),
		);
		try {
			const lvResponse = await queryResult.unwrap();
			return {
				lv: lvResponse,
				gaebData: lvResponse?.gaebData ?? null,
			};
		} catch (err) {
			return rejectWithValue(
				err.message || "Failed to load BoQ editor data",
			);
		} finally {
			queryResult.unsubscribe();
		}
	},
);

const initialState = {
	lv: null,
	gaebData: null,
	/** Whether any lv_position of this LV is referenced by an invoice. */
	hasInvoice: false,
	loading: false,
	error: null,
	hasUnsavedChanges: false,
	/** 'draft' | 'finalized' | null  – see constants/boq-status.js */
	boqStatus: null,
	/** Latest ValidationResult from AVA Cloud (updated after every save, cleared on user edit). */
	validationResult: null,
};

const boqSlice = createSlice({
	name: "boq",
	initialState,
	reducers: {
		setHasUnsavedChanges: (state, { payload }) => {
			state.hasUnsavedChanges = Boolean(payload);
		},
		/** Call whenever the user makes a structural edit - clears the stale validation badge. */
		clearBoQValidation: (state) => {
			state.validationResult = null;
		},
		clearBoQState: () => initialState,
		/** Apply the response of the saveBoQ RTK mutation into editor state. */
		setSaveResult: (state, { payload }) => {
			state.gaebData = payload?.gaebData ?? state.gaebData;
			state.boqStatus = payload?.boqStatus ?? state.boqStatus;
			state.validationResult = payload?.validationResult ?? null;
			state.hasUnsavedChanges = false;
		},
		/** Apply the response of the reopenBoQ RTK mutation into editor state. */
		setReopenResult: (state, { payload }) => {
			state.boqStatus = payload?.boqStatus ?? state.boqStatus;
			state.hasInvoice = false;
		},
		/** Apply the response of the validateBoQ RTK mutation into editor state. */
		setValidationResult: (state, { payload }) => {
			state.validationResult = payload ?? null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchBoQEditorData.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchBoQEditorData.fulfilled, (state, action) => {
				state.loading = false;
				state.lv = action.payload.lv;
				state.gaebData = action.payload.gaebData;
				state.boqStatus = action.payload.lv?.boqStatus ?? null;
				state.hasInvoice = action.payload.lv?.hasInvoice ?? false;
			})
			.addCase(fetchBoQEditorData.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload ?? "Unknown error";
			});
	},
});

export const {
	setHasUnsavedChanges,
	clearBoQValidation,
	clearBoQState,
	setSaveResult,
	setReopenResult,
	setValidationResult,
} = boqSlice.actions;

export const selectBoQLv = (state) => state.boq.lv;
export const selectBoQGaebData = (state) => state.boq.gaebData;
export const selectBoQHasInvoice = (state) => state.boq.hasInvoice;
export const selectBoQLoading = (state) => state.boq.loading;
export const selectBoQError = (state) => state.boq.error;
export const selectBoQHasUnsavedChanges = (state) =>
	state.boq.hasUnsavedChanges;
export const selectBoQStatus = (state) => state.boq.boqStatus;
export const selectBoQValidationResult = (state) => state.boq.validationResult;

export default boqSlice.reducer;
